import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, hasRole } from '../utils/permissions';
import { AUTH_STATES } from '../contexts/AuthContext';

/**
 * Protected Route Component
 * Handles role-based and permission-based route protection
 */
const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  requiredPermission = null, 
  requiredPermissions = [], 
  fallbackPath = '/login',
  showFallback = false 
}) => {
  const { user, isAuthenticated, isLoading, state, initialize } = useAuth();
  
  // Trigger authentication initialization if not started
  useEffect(() => {
    if (state === AUTH_STATES.IDLE) {
      initialize();
    }
  }, [state, initialize]);

  // Show loading state while authentication is being checked
  if (isLoading || state === AUTH_STATES.INITIALIZING) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Checking authentication...</span>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check role-based access
  if (requiredRole && !hasRole(user, requiredRole)) {
    if (showFallback) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to access this page.
            </p>
            <p className="text-sm text-gray-500">
              Required role: <span className="font-semibold">{requiredRole}</span>
            </p>
            <p className="text-sm text-gray-500">
              Your role: <span className="font-semibold">{user.role}</span>
            </p>
          </div>
        </div>
      );
    }
    return <Navigate to="/dashboard" replace />;
  }

  // Check single permission
  if (requiredPermission && !hasPermission(user, requiredPermission)) {
    if (showFallback) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to access this feature.
            </p>
            <p className="text-sm text-gray-500">
              Required permission: <span className="font-semibold">{requiredPermission}</span>
            </p>
          </div>
        </div>
      );
    }
    return <Navigate to="/dashboard" replace />;
  }

  // Check multiple permissions (user must have ALL)
  if (requiredPermissions.length > 0) {
    const hasAllRequired = requiredPermissions.every(permission => 
      hasPermission(user, permission)
    );
    
    if (!hasAllRequired) {
      if (showFallback) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="text-6xl mb-4">🚫</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">
                You don't have all required permissions to access this feature.
              </p>
              <p className="text-sm text-gray-500">
                Required permissions: <span className="font-semibold">{requiredPermissions.join(', ')}</span>
              </p>
            </div>
          </div>
        );
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  // All checks passed, render the protected content
  return children;
};

/**
 * Higher-order component for protecting routes
 */
export const withRoleProtection = (Component, requiredRole) => {
  return (props) => (
    <ProtectedRoute requiredRole={requiredRole}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

/**
 * Higher-order component for protecting routes with permissions
 */
export const withPermissionProtection = (Component, requiredPermission) => {
  return (props) => (
    <ProtectedRoute requiredPermission={requiredPermission}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

/**
 * Component for conditionally rendering content based on permissions
 */
export const PermissionGate = ({ 
  children, 
  requiredRole = null, 
  requiredPermission = null, 
  requiredPermissions = [],
  fallback = null 
}) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return fallback;
  }

  // Check role
  if (requiredRole && !hasRole(user, requiredRole)) {
    return fallback;
  }

  // Check single permission
  if (requiredPermission && !hasPermission(user, requiredPermission)) {
    return fallback;
  }

  // Check multiple permissions
  if (requiredPermissions.length > 0) {
    const hasAllRequired = requiredPermissions.every(permission => 
      hasPermission(user, permission)
    );
    if (!hasAllRequired) {
      return fallback;
    }
  }

  return children;
};

export default ProtectedRoute;
